/**
 * Statement entries table — pure view. Renders the date/description/credit/
 * debit/running-balance grid. Empty state ("No movement in this period.")
 * is owned here too so the shell only branches on `statement != null`.
 *
 * Future-dated rows (one-off OR projected) get softened text + bg so the
 * past/future split is visually consistent regardless of how the entry
 * entered the system. Per-row tag uses `computeTag` from /lib/reconcileTags.
 */
import React from 'react';
import { fmtMoney } from '../../lib/format';
import { TAG_STYLES, TAG_LABELS, computeTag } from '../../lib/reconcileTags';
import { formatDateUK } from './constants';

const EntriesTable = ({ entries, opening_balance, todayIso, showAsCredit }) => {
  if (entries.length === 0) {
    return (
      <div className="px-6 sm:px-10 py-16 text-center" data-testid="no-movement-message">
        <p className="text-sm text-[#64748B]">No movement in this period.</p>
        <p className="text-xs text-[#64748B] mt-1">Opening = closing balance = £{fmtMoney(opening_balance)}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-white border-b border-[#E2E8F0]">
          <tr>
            <th className="text-left px-6 sm:px-10 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Date</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Description</th>
            <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981]">Credit</th>
            <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#EF4444]">Debit</th>
            <th className="text-right px-6 sm:px-10 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] hidden md:table-cell">Balance</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => {
            const isProjected = e.source === 'projected';
            // Treat any future-dated entry — projected OR one-off — with the
            // same softened styling so past vs future is visually
            // consistent regardless of how the entry got there.
            const isFuture = e.date > todayIso;
            const showAsFuture = isProjected || isFuture;
            const isCredit = showAsCredit(e.transaction_type);
            const tag = computeTag({ date: e.date, reconciled: !isProjected && !!e.reconciled }, todayIso);
            return (
              <tr key={e.entry_id} className={`border-b border-[#E2E8F0] last:border-0 ${showAsFuture ? 'bg-[#F8FAFC]/60' : ''}`} data-testid={`statement-row-${i}`}>
                <td className={`px-6 sm:px-10 py-3 text-sm ${showAsFuture ? 'text-[#64748B]' : 'text-[#334155]'}`}>{formatDateUK(e.date)}</td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-medium ${showAsFuture ? 'text-[#64748B] italic' : 'text-[#020617]'}`}>{e.description}</span>
                    {e.transfer_id && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#E0E7FF] text-[#3730A3]" title="Transfer between accounts">
                        Transfer
                      </span>
                    )}
                    {e.is_estimated && (
                      <span className="inline-flex items-center justify-center h-5 px-2 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] leading-none align-middle" title="Estimated amount" data-testid={`estimated-badge-${i}`}>
                        EST
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${TAG_STYLES[tag]}`} data-testid={`statement-tag-${tag}-${i}`}>
                      {TAG_LABELS[tag]}
                    </span>
                    {e.category && (
                      <span className="text-xs text-[#64748B]">· {e.category}</span>
                    )}
                  </div>
                </td>
                <td className={`px-4 py-3 text-sm text-right font-semibold ${isCredit ? 'text-[#10B981]' : 'text-[#CBD5E1]'}`} data-testid={`credit-${i}`}>
                  {isCredit ? `£${fmtMoney(e.amount)}` : '–'}
                </td>
                <td className={`px-4 py-3 text-sm text-right font-semibold ${!isCredit ? 'text-[#EF4444]' : 'text-[#CBD5E1]'}`} data-testid={`debit-${i}`}>
                  {!isCredit ? `£${fmtMoney(e.amount)}` : '–'}
                </td>
                <td className={`px-6 sm:px-10 py-3 text-sm text-right font-medium hidden md:table-cell ${e.running_balance < 0 ? 'text-[#EF4444]' : 'text-[#020617]'}`} data-testid={`balance-${i}`}>
                  £{fmtMoney(e.running_balance)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EntriesTable;
